"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"

type TOCItem = {
  id: string
  title: string
  children?: { id: string; title: string }[]
}

type TableOfContentsProps = {
  items: TOCItem[]
}

export function TableOfContents({ items }: TableOfContentsProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [showLines, setShowLines] = useState(true)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [activeChildId, setActiveChildId] = useState<string | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      // Show TOC after scrolling 100px (same as nav bar)
      setIsVisible(scrollPosition > 100)

      // Track active section
      const scrollPositionWithOffset = scrollPosition + 200 // Offset for better detection
      let foundActive = false
      
      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i]
        
        // First check children (more specific)
        if (item.children) {
          for (let j = item.children.length - 1; j >= 0; j--) {
            const childElement = document.getElementById(item.children[j].id)
            if (childElement) {
              const childTop = childElement.offsetTop
              if (scrollPositionWithOffset >= childTop) {
                setActiveIndex(i)
                setActiveChildId(item.children[j].id)
                foundActive = true
                break
              }
            }
          }
          if (foundActive) break
        }
        
        // Then check parent
        const element = document.getElementById(item.id)
        if (element) {
          const elementTop = element.offsetTop
          if (scrollPositionWithOffset >= elementTop) {
            setActiveIndex(i)
            setActiveChildId(null)
            foundActive = true
            break
          }
        }
      }
      
      if (!foundActive) {
        setActiveIndex(null)
        setActiveChildId(null)
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check
    return () => window.removeEventListener('scroll', handleScroll)
  }, [items])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const yOffset = -100 // Offset for fixed header
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  if (!isVisible) return null

  const handleMouseEnter = () => {
    setIsHovered(true)
    setShowLines(false)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    // Wait for popup exit animation (200ms) plus extra buffer to prevent flash
    setTimeout(() => {
      setShowLines(true)
    }, 400)
  }

  return (
    <div 
      className="hidden md:block fixed right-8 top-48 z-40 transition-opacity duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative flex items-start">
        {/* Line indicators - hidden when popup is open */}
        {showLines && (
          <div className="relative flex flex-col gap-2 mr-3 items-end">
            {items.flatMap((item, index) => {
              const isActive = activeIndex === index && !activeChildId
              const lines = [
                // Main line
                <div
                  key={item.id}
                  className={`
                    h-0.5 w-4 transition-all
                    ${isActive 
                      ? 'bg-[#3f4141]' 
                      : 'bg-gray-300'
                    }
                  `}
                />,
                // Child lines
                ...(item.children || []).map((child) => {
                  const isChildActive = activeChildId === child.id
                  return (
                    <div
                      key={child.id}
                      className={`
                        h-0.5 w-3 transition-all
                        ${isChildActive 
                          ? 'bg-[#3f4141]' 
                          : 'bg-gray-300'
                        }
                      `}
                    />
                  )
                })
              ]
              return lines
            })}
          </div>
        )}

        {/* Full list on hover */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
              className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[180px] max-w-[200px]"
            >
              <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                Sections
              </div>
              <div className="py-1">
                {items.map((item, index) => {
                  const isActive = activeIndex === index && !activeChildId
                  
                  return (
                    <div key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`
                          w-full text-left px-3 py-1.5 text-sm transition-colors
                          ${isActive 
                            ? 'text-[#3f4141] font-medium bg-gray-50' 
                            : 'text-gray-600 hover:text-[#3f4141] hover:bg-gray-50'
                          }
                        `}
                      >
                        {item.title}
                      </button>
                      {/* Sub-headings */}
                      {item.children && item.children.map((child) => {
                        const isChildActive = activeChildId === child.id
                        return (
                          <button
                            key={child.id}
                            onClick={() => scrollToSection(child.id)}
                            className={`
                              w-full text-left px-3 py-1 pl-6 text-xs transition-colors
                              ${isChildActive 
                                ? 'text-[#3f4141] font-medium bg-gray-50' 
                                : 'text-gray-500 hover:text-[#3f4141] hover:bg-gray-50'
                              }
                            `}
                          >
                            {child.title}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

